import { AuthApiS } from '@/features/Auth/outbound/share';
import type {
  CietyWithdrawCheckData,
  MarppleWithdrawCheckData,
  MembershipWithdrawCheckData,
} from '@/features/Auth/type';
import { SERVICE, SERVICE_CONTACT } from '@/libs/constant';
import { T } from '@/libs/translation';
import { postUserWithdrewMessage } from '@/libs/util/openerPostMessage';
import { Button, Typo } from '@/ui/component/atom';
import { Header, Layout } from '@/ui/component/cell';
import { LogoView, makeErrorPopup } from '@/ui/component/view';
import { openPopup } from '@/ui/component/view/Popup/openPopup';
import { commify, getSharedData, PostMessage, type SharedData } from '@/ui/util';
import { entries, filter, identity, join, map, pipe, take, toArray } from '@fxts/core';
import { isAxiosError } from 'axios';
import { html, Page, View } from 'rune-ts';
import klass from './WithdrawalPage.module.scss';

export interface WithdrawalPageData {
  marpple?: MarppleWithdrawCheckData;
  ciety?: CietyWithdrawCheckData;
  membership?: MembershipWithdrawCheckData;
  can_withdraw: boolean;
  open?: 'true';
}

export interface WithdrawalPageOption {
  klass?: string;
}

class WithdrawalPageBodyView extends View<WithdrawalPageData> {
  cancel: Button;
  withdrawal: Button;

  constructor(
    data: WithdrawalPageData,
    public option: WithdrawalPageOption = {},
  ) {
    super(data);

    this.cancel = new Button(
      { label: new Typo({ text: T('common.cancel') }, { typo: 'p1' }) },
      { klass: klass.cancel, color: 'line', size: 'large' },
    );

    this.withdrawal = new Button(
      { label: new Typo({ text: T('withdrawal.go') }, { typo: 'p1' }) },
      { klass: klass.withdrawal, size: 'large' },
    );
  }

  override template() {
    const { device, theme } = getSharedData(this);

    return html`
      <div class="${this.option.klass ?? ''} ${klass.withdrawal_page}">
        ${device === 'pc'
          ? html` <div class="${klass.title}">
              ${new LogoView({ theme })} ${new Typo({ text: T('withdrawal.title') }, { typo: 'p2', color: 'gray_50' })}
            </div>`
          : ''}

        <div class="${klass.guide_container}">
          <div class="${klass.guide_contents}">
            ${new Typo({ text: T('withdrawal.common.001') }, { typo: 'p1', weight: 'bold' })}
            ${new Typo({ text: T('withdrawal.common.002') }, { typo: 'p2', color: 'black' })}
          </div>
          <div class="${klass.guide}">
            ${theme === 'Ciety'
              ? html`${this.cietyInfoTemplate()} ${this.marppleInfoTemplate()}`
              : html`${this.marppleInfoTemplate()} ${this.cietyInfoTemplate()}`}
            <div class="${klass.guide}">
              <div class="${klass.guide_contents}">
                ${new Typo(
                  {
                    text: html.preventEscape(T('withdrawal.common.003', { contact: this.getContact() })),
                  },
                  { typo: 'p2', color: 'gray_80' },
                )}
                ${new Typo({ text: html.preventEscape(T('withdrawal.common.004')) }, { typo: 'p2', color: 'gray_80' })}
              </div>
            </div>
          </div>
        </div>
        <div class="${klass.buttons}">${this.cancel} ${this.withdrawal}</div>
      </div>
    `;
  }

  private cietyInfoTemplate() {
    const { alone_daos } = this.data.ciety ?? { alone_daos: [] };
    const { dao } = this.data.membership ?? {};

    return html`<div class="${klass.guide_contents}">
      ${new Typo({ text: T('common.service_Ciety') }, { typo: 'p2', weight: 'bold' })}
      <ol class="${klass.list} ${klass.guide_contents}">
        ${new Typo({ text: T('withdrawal.ciety.001') }, { typo: 'p2', color: 'gray_80', tag: 'li' })}
        ${new Typo({ text: T('withdrawal.ciety.002') }, { typo: 'p2', color: 'gray_80', tag: 'li' })}
      </ol>
      ${alone_daos?.length > 0 || dao
        ? html` <div class="${klass.guide_info} ${Typo.color.gray_80}">
            ${alone_daos?.length > 0
              ? html`
                  <div class="${klass.item}">
                    ${new Typo({ text: T('withdrawal.ciety.003') }, { typo: 'p2' })}
                    ${new Typo(
                      { text: T('common.count', { value: commify(alone_daos?.length ?? 0) }) },
                      { typo: 'p2', color: 'accent' },
                    )}
                  </div>
                `
              : ''}
            ${dao
              ? html`
                  <div class="${klass.item}">
                    ${new Typo({ text: T('withdrawal.ciety.004') }, { typo: 'p2' })}
                    ${new Typo({ text: T('common.count', { value: 1 }) }, { typo: 'p2', color: 'accent' })}
                  </div>
                `
              : ''}
          </div>`
        : ''}
    </div>`;
  }

  private marppleInfoTemplate() {
    return html`<div class="${klass.guide_contents}">
      ${new Typo(
        { text: `${T('common.service')} · ${T('common.service_MarppleShop')}` },
        { typo: 'p2', weight: 'bold' },
      )}
      <ol class="${klass.list} ${klass.guide_contents} ${Typo.color.gray_80} ${Typo.typo.p2}">
        ${new Typo({ text: T('withdrawal.marpple.001') }, { typo: 'p2', color: 'gray_80', tag: 'li' })}
        ${this.data.marpple?.store
          ? new Typo(
              {
                text: html.preventEscape(
                  T('withdrawal.marpple.002', {
                    highlight: `<span class="${Typo.color.black}">${T('withdrawal.marpple.002_1')}</span>`,
                  }),
                ),
              },
              { typo: 'p2', tag: 'li' },
            )
          : ''}
        ${new Typo({ text: T('withdrawal.marpple.003') }, { typo: 'p2', color: 'gray_80', tag: 'li' })}
      </ol>
      <div class="${klass.guide_info} ${Typo.color.gray_80}">
        ${this.data.marpple?.store
          ? html` <div class="${klass.item}">
                ${new Typo({ text: T('withdrawal.marpple.004') }, { typo: 'p2' })}
                ${new Typo(
                  { text: T('common.issue', { value: commify(this.data.marpple.store?.current_order_count ?? 0) }) },
                  { typo: 'p2', color: 'accent' },
                )}
              </div>
              <div class="${klass.item}">
                ${new Typo({ text: T('withdrawal.marpple.005') }, { typo: 'p2' })}
                ${new Typo(
                  { text: T('common.count', { value: commify(this.data.membership?.membership_count ?? 0) }) },
                  { typo: 'p2', color: 'accent' },
                )}
              </div>
              <div class="${klass.item}">
                ${new Typo({ text: T('withdrawal.marpple.006') }, { typo: 'p2' })}
                ${new Typo(
                  { text: T('common.issue', { value: commify(this.data.membership?.commission_request_count ?? 0) }) },
                  { typo: 'p2', color: 'accent' },
                )}
              </div>
              <div class="${klass.item}">
                ${new Typo({ text: T('withdrawal.marpple.007') }, { typo: 'p2' })}
                ${new Typo(
                  {
                    text: T('common.money', { value: commify(this.data.marpple.store?.remain_adjustment_amount ?? 0) }),
                  },
                  { typo: 'p2', color: 'accent' },
                )}
              </div>`
          : ''}
        <div class="${klass.item}">
          ${new Typo({ text: T('withdrawal.marpple.008') }, { typo: 'p2' })}
          ${new Typo(
            { text: T('common.point', { value: commify(this.data.marpple?.point ?? 0) }) },
            { typo: 'p2', color: 'black' },
          )}
        </div>
      </div>
    </div>`;
  }

  private getContact(_theme?: SharedData['theme']) {
    const { theme } = getSharedData(this);

    return pipe(
      SERVICE_CONTACT[_theme ?? theme],
      entries,
      filter(([_, v]) => v),
      map(([key, v]) => `<a href="${key}://${v}" class="${Typo.color.accent} ${Typo.deco.underline}">${v}</a>`),
      join(` ${T('common.or')} `),
    );
  }

  override onRender() {
    this.cancel.addEventListener('click', () => {
      history.back();
    });

    this.withdrawal.addEventListener('click', this.withdraw.bind(this));
  }

  private async withdraw() {
    if (!this.data.can_withdraw) {
      return pipe(
        this.getWithdrawCheckSequence(),
        map((fn) => fn()),
        filter(identity),
        take(1),
        ([res]) => openPopup(res),
      );
    }

    try {
      const res = await openPopup(this.beforeWithdrawCheck(), { content_full: true });

      if (res !== 'confirm') {
        return;
      }

      const { data } = await AuthApiS.postWithdraw();

      await openPopup({ title: T('withdrawal.common.008'), message: T('withdrawal.common.004') });

      postUserWithdrewMessage();

      PostMessage.withdraw();

      const { app_info } = getSharedData(this);

      if (app_info?.name === 'MarppleShop') {
        return PostMessage.resetHome();
      }

      if (this.data.open === 'true') {
        return window.close();
      }

      location.replace(data.redirect_url);
    } catch (e) {
      if (isAxiosError(e)) {
        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e);
    }
  }

  private getWithdrawCheckSequence() {
    const { theme } = getSharedData(this);

    if (theme === SERVICE.Ciety) {
      return [
        this.checkCietyCommunityAlone.bind(this),
        this.checkCietyCommunityConnectMarppleShop.bind(this),
        this.checkCreator.bind(this),
      ];
    }

    return [
      this.checkCreator.bind(this),
      this.checkCietyCommunityAlone.bind(this),
      this.checkCietyCommunityConnectMarppleShop.bind(this),
    ];
  }

  private checkCietyCommunityAlone() {
    if (!this.data.ciety?.can_withdraw && this.data.ciety?.alone_daos && this.data.ciety.alone_daos.length > 0) {
      const { theme } = getSharedData(this);
      const rest_community_length = this.data.ciety.alone_daos.length - 3;

      return {
        title: T('withdrawal.common.005'),
        message: html`<div class="${klass.guide_contents}">
          ${new Typo(
            { text: html.preventEscape(T('withdrawal.ciety.005')) },
            theme === 'Marpple' ? { typo: 'p1' } : { typo: 'p2', color: 'gray_80' },
          )}
          <div class="${klass.withdraw_info}">
            ${new Typo(
              { text: `${T('withdrawal.ciety.003')} ${commify(this.data.ciety.alone_daos.length)}` },
              { typo: 'p2', color: 'accent' },
            )}
            <div class="${klass.guide_contents}">
              ${pipe(
                this.data.ciety.alone_daos,
                map((community) => new Typo({ text: community.dao.displayName }, { typo: 'p2', klass: klass.item })),
                take(3),
                toArray,
              )}
            </div>
            ${rest_community_length > 0
              ? new Typo(
                  { text: T('withdrawal.ciety.006', { value: commify(rest_community_length) }) },
                  { typo: 'p2', color: 'gray_80' },
                )
              : ''}
          </div>
        </div>`,
      };
    }
  }

  private checkCietyCommunityConnectMarppleShop() {
    if (!this.data.membership?.can_withdraw && this.data.membership?.dao?.displayName) {
      const { theme } = getSharedData(this);

      return {
        title: T('withdrawal.common.005'),
        message: html`<div class="${klass.guide_contents}">
          ${new Typo(
            { text: html.preventEscape(T('withdrawal.ciety.007')) },
            theme === 'Marpple' ? { typo: 'p1' } : { typo: 'p2', color: 'gray_80' },
          )}
          <div class="${klass.withdraw_info}">
            ${new Typo({ text: T('withdrawal.ciety.004') }, { typo: 'p2', color: 'accent' })}
            <div class="${klass.guide_contents}">
              ${new Typo({ text: this.data.membership.dao.displayName }, { typo: 'p2', klass: klass.item })}
            </div>
          </div>
        </div>`,
      };
    }
  }

  private checkCreator() {
    if (
      !this.data.marpple?.can_withdraw ||
      (!this.data.membership?.can_withdraw &&
        (this.data.membership?.membership_count || this.data.membership?.commission_request_count))
    ) {
      return {
        title: T('withdrawal.common.005'),
        message: T('withdrawal.marpple.009', {
          contact: this.getContact(SERVICE.MarppleShop),
        }),
      };
    }
  }

  private beforeWithdrawCheck() {
    if (this.data.ciety?.transfer_daos && this.data.ciety.transfer_daos.length > 0) {
      const { theme } = getSharedData(this);
      const rest_community_length = this.data.ciety.transfer_daos.length - 3;

      return {
        title: T('withdrawal.ciety.008'),
        message: html`<div class="${klass.popup_guide_contents}">
          ${new Typo(
            { text: html.preventEscape(T('withdrawal.ciety.009')) },
            theme === 'Marpple' ? { typo: 'p1' } : { typo: 'p2', color: 'gray_80' },
          )}
          <div class="${klass.withdraw_info}">
            ${new Typo(
              { text: `${T('withdrawal.ciety.010')} ${commify(this.data.ciety.transfer_daos.length)}` },
              { typo: 'p2', color: 'accent' },
            )}
            <div class="${klass.guide_contents}">
              ${pipe(
                this.data.ciety.transfer_daos,
                map(
                  (dao) =>
                    html`<div>
                      ${new Typo({ text: dao.dao.displayName }, { typo: 'p2', klass: klass.item })}
                      ${new Typo({ text: dao.member.nickname }, { typo: 'p3', klass: klass.sub_item, color: 'accent' })}
                    </div>`,
                ),
                take(3),
                toArray,
              )}
            </div>
            ${rest_community_length > 0
              ? new Typo(
                  { text: T('withdrawal.ciety.011', { value: commify(rest_community_length) }) },
                  { typo: 'p2', color: 'gray_80' },
                )
              : ''}
          </div>
        </div>`,
        ok: T('withdrawal.ciety.012'),
        cancel: T('common.cancel'),
      };
    }

    return {
      title: T('withdrawal.common.006'),
      message: T('withdrawal.common.007'),
      ok: T('common.yes'),
      cancel: T('common.cancel'),
    };
  }
}

export class WithdrawalPage extends Page<WithdrawalPageData> {
  layout: Layout;

  constructor(
    data: WithdrawalPageData,
    sharedData: SharedData,
    public option: WithdrawalPageOption = {},
  ) {
    super(data, sharedData);

    this.layout = new Layout(
      { view: new WithdrawalPageBodyView(this.data) },
      {
        fullwidth: true,
        header:
          sharedData.device === 'mo'
            ? new Header({
                title: new Typo({ text: T('withdrawal.title') }, { typo: 'h3', weight: 'bold' }),
              })
            : undefined,
      },
    );
  }

  override template() {
    return html` <div>${this.layout}</div> `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      history.back();
    });
  }
}
